import createCachedSelector from 're-reselect';
import {getAreaEntities, getAreasByBuildingId} from '../../data/areas';
import {getBuildingEntities, getTotalBuildings} from '../../data/buildings/selectors';
import {getFloorEntities} from '../../data/floors';
import {getAllOpeningDaysExceptions} from '../../data/openingDays';
import {RootState} from '../../rootReducer';
import {
  getAllWorkspaceReservationCountPerNodeOnDate,
  getWorkspaceReservationByDate,
} from '../../data/workspaceReservation';
import {NodeOnDateSelectorProps} from '../../util/buildingNodes/types';
import {getNodeInformation, nodeNotFoundResult} from './helpers';
import {WorkspaceReservation} from '../../data';
import {parseISO} from 'date-fns';
import {NodeInformation} from './types';
import {cachedComplexProps} from '../../util';

const getNodeInformationForAllNodesCacheKey = (input: {
  date?: Date;
  inviteeCount?: number;
  workspaceReservation?: WorkspaceReservation;
}): string => {
  const {date, inviteeCount, workspaceReservation} = input;
  return [date?.getTime() ?? 0, inviteeCount, workspaceReservation?.id, workspaceReservation?.groupReservationId].join(
    `__`,
  );
};

export const getNodeInformationForAllNodes = createCachedSelector(
  getBuildingEntities,
  getFloorEntities,
  getAreaEntities,
  (state: RootState) => state.ui.workspaceReservation.invitees,
  getAllWorkspaceReservationCountPerNodeOnDate,
  getAllOpeningDaysExceptions,
  (
    _state: RootState,
    date: Date | undefined,
    inviteeCount: number | undefined,
    workspaceReservation?: WorkspaceReservation,
  ) => cachedComplexProps({date, inviteeCount, workspaceReservation}, getNodeInformationForAllNodesCacheKey),
  (
    buildings,
    floors,
    areas,
    invitees,
    workspaceReservationCountPerNodeByDate,
    openingDaysExceptions,
    {date, inviteeCount, workspaceReservation},
  ) => {
    // Excluding non confirmed and non pending invitation from occupation
    const numberOfInvitees =
      invitees.filter(
        (invitee) => !invitee.status || invitee.status === 'Confirmed' || invitee.status === 'InvitationPending',
      ).length || inviteeCount;

    const areasInfo = Object.fromEntries(
      Object.keys(areas).map((nodeId) => [
        nodeId,
        getNodeInformation({
          nodeId,
          date,
          buildings,
          floors,
          areas,
          workspaceReservationCountPerNodeByDate,
          openingDaysExceptions,
          numberOfInvitees,
          workspaceReservation,
        }),
      ]),
    );

    const floorsInfo = Object.fromEntries(
      Object.keys(floors).map((nodeId) => [
        nodeId,
        getNodeInformation({
          nodeId,
          date,
          buildings,
          floors,
          areas,
          workspaceReservationCountPerNodeByDate,
          openingDaysExceptions,
          numberOfInvitees,
          workspaceReservation,
          childInfos: areasInfo,
        }),
      ]),
    );

    // Note: mutate for efficiency
    const areasAndFloorsInfo = Object.assign(areasInfo, floorsInfo);

    const buildingsInfo = Object.fromEntries(
      Object.keys(buildings).map((nodeId) => [
        nodeId,
        getNodeInformation({
          nodeId,
          date,
          buildings,
          floors,
          areas,
          workspaceReservationCountPerNodeByDate,
          openingDaysExceptions,
          numberOfInvitees,
          workspaceReservation,
          childInfos: areasAndFloorsInfo,
        }),
      ]),
    );

    // Note: mutate for efficiency
    const areasAndFloorsAndBuildingInfo = Object.assign(areasAndFloorsInfo, buildingsInfo);

    return areasAndFloorsAndBuildingInfo;
  },
)((_, date, inviteeCount, workspaceReservation) =>
  getNodeInformationForAllNodesCacheKey({date, inviteeCount, workspaceReservation}),
);

const getNodeInformationByNodeIdCacheKey = (input: {
  props: NodeOnDateSelectorProps;
  workspaceReservation?: WorkspaceReservation;
}): string => {
  const {props, workspaceReservation} = input;
  return [
    props.date?.getTime() ?? 0,
    props.nodeId,
    props.inviteeCount,
    workspaceReservation?.id,
    workspaceReservation?.groupReservationId,
  ].join(`__`);
};
export const getNodeInformationByNodeId = createCachedSelector(
  (_state: RootState, props: NodeOnDateSelectorProps, _workspaceReservation?: WorkspaceReservation) => props.nodeId,
  (state: RootState, props: NodeOnDateSelectorProps, workspaceReservation?: WorkspaceReservation) =>
    getNodeInformationForAllNodes(state, props.date, props.inviteeCount, workspaceReservation),
  (nodeId, nodeInformationForAllNodes) => {
    // Excluding non confirmed and non pending invitation from occupation
    return nodeId ? nodeInformationForAllNodes[nodeId] ?? nodeNotFoundResult : nodeNotFoundResult;
  },
)((_, props, workspaceReservation) => getNodeInformationByNodeIdCacheKey({props, workspaceReservation}));

export const getNodeInformationPerAreaByBuildingId = createCachedSelector(
  getBuildingEntities,
  getFloorEntities,
  getAreaEntities,
  getAllWorkspaceReservationCountPerNodeOnDate,
  getAllOpeningDaysExceptions,
  (_state: RootState, date: string, _buildingId: string) => date,
  (state: RootState, _date: string, buildingId: string) => getAreasByBuildingId(state, buildingId),
  (buildings, floors, areas, workspaceReservationCountPerNodeByDate, openingDaysExceptions, date, areasForBuilding) => {
    const parsedDate = parseISO(date);

    return areasForBuilding
      .map((area) => ({
        id: area.id,
        info: getNodeInformation({
          nodeId: area.id,
          date: parsedDate,
          buildings,
          floors,
          areas,
          workspaceReservationCountPerNodeByDate,
          openingDaysExceptions,
        }),
      }))
      .reduce((acc, curr) => ({...acc, [curr.id]: curr.info}), {} as {[areaId: string]: NodeInformation | undefined});
  },
)((_, date, buildingId) => `${buildingId}:${date}`);

export const getNodeInformationByNodeIdInEditMode = createCachedSelector(
  (state: RootState, props: NodeOnDateSelectorProps) => {
    const workspaceReservation = getWorkspaceReservationByDate(state, props.date);
    return getNodeInformationByNodeId(state, props, workspaceReservation);
  },
  (nodeInformation) => nodeInformation,
)((_state, {date, inviteeCount, nodeId}) => `${nodeId}:${inviteeCount}:${date?.toISOString()}`);

export const getAreAllNodesFull = createCachedSelector(
  getTotalBuildings,
  getBuildingEntities,
  getFloorEntities,
  getAreaEntities,
  getAllWorkspaceReservationCountPerNodeOnDate,
  getAllOpeningDaysExceptions,
  (_: RootState, date: Date | undefined) => date,
  (buildingCount, buildings, floors, areas, workspaceReservationCountPerNodeByDate, openingDaysExceptions, date) => {
    if (!buildingCount) return false;
    return Object.values(buildings).every((building) => {
      if (!building) {
        return false;
      }

      const {isFull} = getNodeInformation({
        nodeId: building.id,
        date,
        buildings,
        floors,
        areas,
        workspaceReservationCountPerNodeByDate,
        openingDaysExceptions,
      });

      return isFull;
    });
  },
)((_, date) => date?.toISOString() || '');

export const getFreeWorkplaceCount = createCachedSelector(
  getBuildingEntities,
  getFloorEntities,
  getAreaEntities,
  getAllWorkspaceReservationCountPerNodeOnDate,
  getAllOpeningDaysExceptions,
  (_: RootState, date: Date | undefined) => date,
  (buildings, floors, areas, workspaceReservationCountPerNodeByDate, openingDaysExceptions, date) => {
    if (!date) {
      return 0;
    }

    const {occupation, capacity} = Object.values(buildings).reduce(
      (accumulator, building) => {
        if (!building) {
          return {occupation: 0, capacity: 0};
        }

        const {occupation: buildingOccupation, capacity: buildingCapacity} = getNodeInformation({
          nodeId: building.id,
          date,
          buildings,
          floors,
          areas,
          workspaceReservationCountPerNodeByDate,
          openingDaysExceptions,
        });

        accumulator = {
          occupation: accumulator.occupation + buildingOccupation,
          capacity: accumulator.capacity + buildingCapacity,
        };

        return accumulator;
      },
      {occupation: 0, capacity: 0},
    );

    return Math.max(capacity - occupation, 0);
  },
)((_, date) => date?.toISOString() || '');
